import { route } from "./routes.js";
import { z } from "zod";
//#region src/permissions.ts
/**
* `GET /v1/admin/permissions` — the org's authorization vocabulary, read by the role editor.
*
* Producer: `AdminPermissionsController.getPermissions` in `cloud/dp/core-api/src/admin/`, which
* returns `PERMISSION_CATALOG`. That constant is `ORG_PERMISSION_CATALOG` from
* `@zion/sdk-contracts`, re-exported under a local name, so the body is that array verbatim.
* Consumer: the admin app's roles pages, through `PermissionSelector`.
*
* Requires `settings:roles:read`. The route was authenticated-only until #2248; leaving it open
* handed any authenticated principal a free enumeration of every capability the org can grant.
*
* No request schema: the route takes no body, no query and no parameter.
*
* `@zion/sdk-contracts` owns the shape as the interfaces `PermissionCatalogEntry` and
* `PermissionCatalog` and exports no zod schema for either. So this file declares the schema and
* names the contracts interface as its owner; the family test parses a value into a
* `PermissionCatalogEntry`-typed binding, which is the compile-time proof the two agree, and parses
* the live `ORG_PERMISSION_CATALOG` to prove it at runtime.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client. No shape in this file travels in both directions.
*/
/**
* One catalog entry: a permission string, the group heading it renders under, its short label, its
* one-line description, and an optional lucide-react icon name for the group header.
*
* `icon` is read from the first entry of each group; entries after the first in a group may omit
* it. `permission` is the canonical permission string, such as `settings:roles:read`.
*
* Owner: `PermissionCatalogEntry` in `cloud/platform-sdk/contracts/src/permission-catalog.ts`.
*/
const permissionCatalogEntrySchema = z.object({
	permission: z.string(),
	group: z.string(),
	label: z.string(),
	description: z.string(),
	icon: z.string().optional()
});
/**
* `GET /v1/admin/permissions` — the whole org permission catalog. Requires `settings:roles:read`.
*/
const GET_PERMISSIONS_ROUTE = route("GET", "/v1/admin/permissions");
/**
* The body of `GET /v1/admin/permissions` — an ordered array, not an envelope. Order is the
* catalog's own and is what the grouped checkbox UI renders.
*/
const getPermissionsResponseSchema = z.array(permissionCatalogEntrySchema);
/** Every route this file names, for `route-census.ts`. */
const permissionsRoutes = Object.freeze([GET_PERMISSIONS_ROUTE]);
//#endregion
export { GET_PERMISSIONS_ROUTE, getPermissionsResponseSchema, permissionCatalogEntrySchema, permissionsRoutes };

//# sourceMappingURL=permissions.js.map